import { Search as SearchIcon } from '@mui/icons-material';
import { Box, Button, Fade, Paper, Popper, Tooltip } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTextSelectionSearch } from '@context/TextSelectionSearchContext';

/**
 * A floating popover that appears when text is selected.
 * Shows a "Search in Transcripts" button to search for the selected text.
 *
 * @param {Object} props Component props
 * @param {string} props.selectedText The currently selected text
 * @param {Object} props.position Position object with top, left coordinates
 * @param {boolean} props.isVisible Whether the popover should be visible
 * @param {Function} props.onClear Callback to clear the selection
 */
const TextSelectionSearchPopover = ({ selectedText, position, isVisible, onClear }) => {
  const { openSearchWithText, cleanTextForSearch } = useTextSelectionSearch();
  const popoverRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Create a virtual anchor element based on position
  useEffect(() => {
    if (position && isVisible) {
      // Create a virtual element for the Popper
      const virtualEl = {
        getBoundingClientRect: () => ({
          top: position.top,
          left: position.left,
          bottom: position.top + (position.height || 0),
          right: position.left + (position.width || 0),
          width: position.width || 0,
          height: position.height || 0,
          x: position.left,
          y: position.top,
        }),
      };
      setAnchorEl(virtualEl);
    } else {
      setAnchorEl(null);
    }
  }, [position, isVisible]);

  const handleSearchClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (selectedText) {
        openSearchWithText(selectedText);
        onClear?.();
        // Clear the browser selection
        window.getSelection()?.removeAllRanges();
      }
    },
    [selectedText, openSearchWithText, onClear],
  );

  // Prevent popover clicks from clearing selection
  const handlePopoverMouseDown = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Get cleaned preview of search text
  const cleanedText = cleanTextForSearch(selectedText);
  const previewText = cleanedText.length > 30 ? `${cleanedText.substring(0, 30)}...` : cleanedText;

  if (!isVisible || !anchorEl || !cleanedText) {
    return null;
  }

  return (
    <Popper
      open={isVisible}
      anchorEl={anchorEl}
      placement="top"
      transition
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 8,
          },
        },
      ]}
      sx={{ zIndex: 1300 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={150}>
          <Paper
            ref={popoverRef}
            elevation={8}
            onMouseDown={handlePopoverMouseDown}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            }}
          >
            <Tooltip title={`Search for: "${previewText}"`} placement="top" arrow>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchIcon fontSize="small" />}
                onClick={handleSearchClick}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  backgroundColor: '#7d5ae8',
                  '&:hover': {
                    backgroundColor: '#6a4bc7',
                  },
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                }}
              >
                Search in Transcripts
              </Button>
            </Tooltip>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};

/**
 * Wrapper component that combines text selection detection with the search popover.
 * Wrap your content that should support highlight-to-search with this component.
 *
 * @example
 * <TextSelectionSearchWrapper>
 *   <Typography>Text that can be selected and searched</Typography>
 * </TextSelectionSearchWrapper>
 */
export const TextSelectionSearchWrapper = ({ children, disabled = false }) => {
  const [selectedText, setSelectedText] = useState('');
  const [popoverPosition, setPopoverPosition] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const timeoutRef = useRef(null);

  const clearSelection = useCallback(() => {
    setSelectedText('');
    setPopoverPosition(null);
    setIsVisible(false);
  }, []);

  const handleMouseUp = useCallback(() => {
    if (disabled) return;

    // Small delay to ensure selection is finalized
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const selection = window.getSelection();

      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        clearSelection();
        return;
      }

      const text = selection.toString().trim();

      // Minimum 2 characters
      if (text.length < 2) {
        clearSelection();
        return;
      }

      // Check if selection is within our container
      const range = selection.getRangeAt(0);
      const containerElement = containerRef.current;

      if (containerElement && !containerElement.contains(range.commonAncestorContainer)) {
        clearSelection();
        return;
      }

      // Get the bounding rect of the selection
      const rect = range.getBoundingClientRect();

      const position = {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX + rect.width / 2,
        width: rect.width,
        height: rect.height,
      };

      setSelectedText(text);
      setPopoverPosition(position);
      setIsVisible(true);
    }, 100);
  }, [disabled, clearSelection]);

  const handleMouseDown = useCallback(
    (_e) => {
      // Only clear if clicking inside the container but not on the popover
      // The popover handles its own mousedown
      if (isVisible) {
        // Give a small delay to check if it's a popover click
        setTimeout(() => {
          const selection = window.getSelection();
          if (!selection || selection.isCollapsed) {
            clearSelection();
          }
        }, 50);
      }
    },
    [isVisible, clearSelection],
  );

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        clearSelection();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [clearSelection]);

  // Handle escape key to clear selection
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isVisible) {
        clearSelection();
        window.getSelection()?.removeAllRanges();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, clearSelection]);

  return (
    <Box
      ref={containerRef}
      onMouseUp={handleMouseUp}
      onMouseDown={handleMouseDown}
      sx={{ position: 'relative' }}
    >
      {children}
      <TextSelectionSearchPopover
        selectedText={selectedText}
        position={popoverPosition}
        isVisible={isVisible}
        onClear={clearSelection}
      />
    </Box>
  );
};

export default TextSelectionSearchPopover;
